import React, { useState } from "react";
import { Box, Button, ButtonBase, Popover, useTheme } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useTranslation } from "react-i18next";

type MonthYearFilterProps = {
  /** Mes seleccionado (1-12) o "" si no hay filtro de mes. */
  month: string;
  /** Año seleccionado o "" si no hay filtro de año. */
  year: string;
  onChange: (value: { month: string; year: string }) => void;
  /** Etiqueta corta de 3 letras para un mes (1-12). */
  formatMonth: (month: number) => string;
};

// Años ofrecidos: tres hacia atrás y uno hacia delante desde el actual.
const getYearOptions = () => {
  const current = new Date().getFullYear();
  return Array.from({ length: 5 }, (_, i) => current - 3 + i);
};

/**
 * Filtro de mes/año en un único control estrecho. Mes y año son
 * independientes: se puede filtrar solo por uno de ellos, y pulsar de nuevo
 * sobre el valor elegido lo quita.
 */
const MonthYearFilter: React.FC<MonthYearFilterProps> = ({
  month,
  year,
  onChange,
  formatMonth,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const label = [
    month ? formatMonth(Number(month)) : "",
    year ? (month ? String(year).slice(-2) : String(year)) : "",
  ]
    .filter(Boolean)
    .join(" ");

  const optionSx = (selected: boolean) => ({
    borderRadius: "20px",
    py: 0.75,
    fontSize: "0.8rem",
    fontWeight: selected ? 700 : 500,
    color: selected ? "primary.contrastText" : "text.primary",
    backgroundColor: selected ? "primary.main" : "transparent",
    "&:hover": {
      backgroundColor: selected ? "primary.main" : theme.palette.grey[100],
    },
  });

  return (
    <>
      <ButtonBase
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          width: "100%",
          height: 40,
          pl: 1.75,
          pr: 0.75,
          borderRadius: "20px",
          border: `1px solid ${theme.palette.grey[300]}`,
          backgroundColor: theme.palette.background.paper,
          display: "flex",
          justifyContent: "space-between",
          fontSize: "0.875rem",
          color: "text.primary",
          whiteSpace: "nowrap",
          "&:hover": { borderColor: theme.palette.text.primary },
        }}
      >
        <span>{label}</span>
        <ArrowDropDownIcon sx={{ color: "action.active" }} />
      </ButtonBase>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { borderRadius: 3, mt: 0.5 } } }}
      >
        <Box sx={{ p: 1.5, width: 240 }}>
          <Box sx={{ display: "flex", gap: 0.5, mb: 1 }}>
            {getYearOptions().map((yr) => {
              const selected = year === String(yr);
              return (
                <ButtonBase
                  key={yr}
                  onClick={() =>
                    onChange({ month, year: selected ? "" : String(yr) })
                  }
                  sx={{ ...optionSx(selected), flex: 1 }}
                >
                  {String(yr).slice(-2)}
                </ButtonBase>
              );
            })}
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 0.5,
              pt: 1,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
              const selected = month === String(m);
              return (
                <ButtonBase
                  key={m}
                  onClick={() =>
                    onChange({ month: selected ? "" : String(m), year })
                  }
                  sx={optionSx(selected)}
                >
                  {formatMonth(m)}
                </ButtonBase>
              );
            })}
          </Box>
          {(month || year) && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Button
                size="small"
                color="inherit"
                onClick={() => onChange({ month: "", year: "" })}
                sx={{ textTransform: "none", borderRadius: "20px" }}
              >
                {t("common.clear", "Clear")}
              </Button>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default MonthYearFilter;
